import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { DataService } from 'api/DataService'
import { BillingRule, PriceNotifications } from 'services/BillingRule'

import { formatMoney } from 'utils/FormatMoney'
import { toastProps } from 'utils/types/toast-props'

import { TimelineRecord } from '../../types/invoiceTimeline'
import { isScheduled } from '../InvoiceTimeline/resendOptions'

/**
 * SC-10890: reenvio de notificação da timeline, com confirmação de custo.
 *
 * A lógica vinha do accordion antigo (e é irmã da do ModalDetail do feed do
 * cliente): as tarifas são buscadas na API a cada abertura, e não lidas das
 * capabilities do JWT, porque o coexistence pode ser ligado ou cair com o
 * usuário já logado e o token só renova no login.
 */
const useResendNotification = (enabled: boolean, onSuccess?: () => void) => {
  const [fees, setFees] = useState<PriceNotifications>()
  const [target, setTarget] = useState<TimelineRecord | null>(null)
  const [sending, setSending] = useState(false)

  useEffect(() => {
    if (!enabled || fees) return
    BillingRule.getPriceNotifications().then((resp) => setFees(resp?.record))
  }, [enabled, fees])

  const feeFor = (medium?: string): string | undefined => {
    if (medium === 'E-mail') return fees?.email_fee
    if (medium === 'SMS') return fees?.sms_fee
    if (medium === 'WhatsApp') {
      return fees?.whatsapp_coexistence_active ? undefined : fees?.whatsapp_fee
    }
    return undefined
  }

  const description = (() => {
    if (!target) return ''

    const medium = target.medium || 'este canal'
    // Pelo STATUS, não pela presença de `scheduled_for`: o campo continua
    // preenchido depois do envio, e o diálogo dizia "está agendada" para uma
    // notificação já enviada.
    if (isScheduled(target)) {
      return `Essa notificação via ${medium} está agendada. Deseja enviá-la agora? Não haverá cobrança adicional.`
    }

    const fee = feeFor(target.medium)
    // Só afirma que é grátis com as tarifas já carregadas: tarifa ausente por
    // falha na chamada não é isenção.
    if (fees && !fee) {
      return `Ao reenviar essa notificação via ${medium}, não haverá cobrança adicional.`
    }
    if (fees && fee) {
      // A API devolve a tarifa crua ("0.29"); sem formatar, o diálogo pedia
      // confirmação de um valor em formato americano, sem símbolo de moeda.
      return `Ao reenviar essa notificação via ${medium}, será descontado ${formatMoney(
        fee
      )} do seu saldo.`
    }
    return `Ao reenviar essa notificação via ${medium}, será descontado o valor da notificação extra do seu saldo.`
  })()

  const confirm = useCallback(async () => {
    if (!target) return

    setSending(true)
    const toastId = toast.loading(
      target.scheduledFor
        ? 'Enviando notificação...'
        : 'Reenviando notificação...'
    )

    try {
      const response = await DataService({
        url: `/v1/messenger_trackings/${target.uuid}/resend`,
        type: 'POST'
      })
      const ok = response?.data?.error === false || !response.error

      toast.update(toastId, {
        render: response.error
          ? response.message
          : response?.data?.message || 'Notificação reenviada com sucesso.',
        type: ok ? 'success' : 'error',
        ...toastProps
      })

      if (ok) onSuccess?.()
    } catch (err) {
      toast.update(toastId, {
        render: 'Erro ao reenviar notificação. Tente novamente.',
        type: 'error',
        ...toastProps
      })
    }

    setSending(false)
    setTarget(null)
  }, [target, onSuccess])

  return {
    target,
    request: setTarget,
    cancel: () => setTarget(null),
    confirm,
    description,
    sending,
    isScheduled: !!target && isScheduled(target)
  }
}

export default useResendNotification
